import flood from 'assets/FloodStageIcons/flood_stage_system.png';
import default1 from 'assets/icons/Default System Icon 1.png';
import default2 from 'assets/icons/Default System Icon 2.png';
import background from 'assets/icons/Default System Icon Background.png';
import classNames from 'classnames';
import BaseTableJs from 'components/Tables/BaseTable';
import React, { useMemo, useState } from 'react';
import { BsCheck } from 'react-icons/bs';
import { FaBellSlash } from 'react-icons/fa';
import {
  getAlarmStateLabel,
  isSilenced,
  parseKey,
  shortenHeadline,
  sourceStringCorrection,
  timeSinceActivity,
} from '../../Alarms/utils/alarmUtils';
import '../assets/AlarmsTable.css';
import type { Alarm } from '../types/Alarms.types';
import { AlarmDropdown } from './AlarmDropdown/AlarmDropdown';
import { AlarmEllipses } from './AlarmEllipses';

const BaseTable = BaseTableJs as any;

const iconMap: Record<string, string> = {
  Flood: flood,
  'Fusion User': default1,
  'global-actions': default1,
  'Overwatch AI': default2,
  Other: background,
};

interface AlarmsTableProps {
  alarms: Alarm[];
  isLoading?: boolean;
  isError?: boolean;
  customEmptyMessage?: string;
  selectedRowIds: number[];
  setSelectedRowIds: React.Dispatch<React.SetStateAction<number[]>>;
  handleAcknowledge?: (alarm: Alarm) => Promise<boolean>;
  handleSilence?: (alarm: Alarm) => Promise<boolean>;
  handleUnsilence?: (alarm: Alarm) => Promise<boolean>;
  handleShare?: (alarm: Alarm) => void;
}

export default function AlarmsTable({
  alarms,
  isLoading,
  isError,
  customEmptyMessage,
  selectedRowIds,
  setSelectedRowIds,
  handleAcknowledge,
  handleSilence,
  handleUnsilence,
  handleShare,
}: AlarmsTableProps) {
  const columns = useMemo(
    () => [
      {
        header: '',
        id: 'Select',
        columnDefType: 'display',
        enableSorting: false,
        size: 50,
        Cell: ({ row }: any) => {
          const alarmId = row.original.id;
          const isSelected = selectedRowIds.includes(alarmId);
          return (
            <div
              data-testid={`alarm-select-${alarmId}`}
              className={classNames(
                isSelected ? 'circle-fill' : 'circle-border',
                'center-content',
                'ms-2',
              )}
              style={{ '--radius': isSelected ? '16px' : '15px' } as React.CSSProperties}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedRowIds((current) =>
                  current.includes(alarmId)
                    ? current.filter((id) => id !== alarmId)
                    : [...current, alarmId],
                );
              }}
            >
              {isSelected && <BsCheck color={'black'} size={20} />}
            </div>
          );
        },
      },
      {
        header: 'State',
        accessorKey: 'state',
        Cell: ({ cell, row }: any) => {
          const value = cell.getValue();
          const label = getAlarmStateLabel(value);
          return (
            <div className="d-flex align-items-center gap-2 text-white fw-bold fs-6">
              {label}
              {isSilenced(row.original) && (handleSilence || handleUnsilence) && (
                <FaBellSlash className="ms-1" title="Silenced" />
              )}
            </div>
          );
        },
      },
      {
        header: 'Source',
        accessorKey: 'source',
        Cell: ({ cell }: any) => {
          const source = cell.getValue();
          const imgSrc = iconMap[source] || iconMap.Other;
          return (
            <span className="d-flex align-items-center gap-2 text-white fs-6">
              <span
                className="circle-fill circle-border center-content alarm-source-icon"
                style={{ '--radius': '22px' } as React.CSSProperties}
              >
                <img src={imgSrc} alt="source-icon" width={25} height={25} />
              </span>
              {sourceStringCorrection(source)}
            </span>
          );
        },
      },
      {
        header: 'Device',
        accessorKey: 'device',
        Cell: ({ cell }: any) => <span className="text-white fs-6">{cell.getValue()}</span>,
      },
      {
        header: 'Type',
        accessorKey: 'info',
        Cell: ({ cell }: any) => (
          <span className="text-white fs-6">{shortenHeadline(cell.getValue().type)}</span>
        ),
      },
      {
        header: 'Activity',
        accessorKey: 'activity',
        Cell: ({ cell }: any) => (
          <span className="text-white fs-6">{new Date(cell.getValue()).toLocaleString()}</span>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        Cell: ({ row }: any) => (
          <span className="text-white fs-6">{timeSinceActivity(row.original.activity)}</span>
        ),
      },
      {
        header: '',
        id: 'Actions',
        columnDefType: 'display',
        enableSorting: false,
        Cell: ({ row }: any) => {
          const alarm = row.original;
          return (
            <AlarmEllipses
              alarm={alarm}
              onAcknowledge={handleAcknowledge}
              onSilence={handleSilence}
              onUnsilence={handleUnsilence}
              onShare={handleShare}
              enableAcknowledge={!!handleAcknowledge} // set to false to disable
              enableSilence={!!handleSilence} // set to false to disable
              enableUnsilence={!!handleUnsilence} // set to false to disable
              enableShare={!!handleShare} // set to false to disable
            />
          );
        },
      },
    ],
    [selectedRowIds, handleAcknowledge, handleSilence, handleUnsilence, handleShare],
  );

  // Use the numeric alarm.id for row identity
  const rows = useMemo(() => alarms.map((alarm) => ({ ...alarm, id: alarm.id })), [alarms]);

  let emptyMessage = customEmptyMessage;
  if (isLoading) emptyMessage = 'Loading…';
  if (isError) emptyMessage = 'Failed to load alarms.';
  if (!isLoading && !isError && rows.length === 0) emptyMessage = 'No alarms found.';

  // Use AlarmDropdown component for expanded row
  const dropdownMenu = (alarm: Alarm) => <AlarmDropdown alarm={alarm} />;

  return (
    <div className="d-flex flex-column h-100 min-vh-0">
      <div className="flex-grow-1 overflow-auto min-vh-0">
        <BaseTable
          rows={rows}
          columns={columns}
          customEmptyMessage={emptyMessage}
          enableRowVirtualization={false}
          selectedRowIds={selectedRowIds}
          expandable={true}
          dropdownMenu={dropdownMenu}
          customStyles={{
            cell: {
              cursor: 'pointer',
            },
          }}
        />
      </div>
    </div>
  );
}
